import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import { ShieldAlert, ChevronRight } from "lucide-react";

const STATUS_TONES = {
    requested: "bg-amber-100 text-amber-800",
    approved: "bg-blue-100 text-blue-700",
    picked_up: "bg-violet-100 text-violet-700",
    inspected: "bg-violet-100 text-violet-700",
    refunded: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
    cancelled: "bg-zinc-100 text-zinc-700",
};

const STATUS_FILTERS = ["all", "requested", "approved", "picked_up", "inspected", "refunded", "rejected"];

const AdminReturns = () => {
    const [status, setStatus] = useState("requested");
    const [flagged, setFlagged] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (status !== "all") params.set("status", status);
        if (flagged) params.set("flagged", "true");
        api.get(`/admin/returns?${params.toString()}`).then((r) => {
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        }).finally(() => setLoading(false));
    };
    useEffect(load, [status, flagged]); // eslint-disable-line

    return (
        <AdminAuthGuard>
            <AdminLayout>
                <div className="mb-6 flex items-end justify-between gap-3">
                    <div>
                        <p className="label-uppercase text-zinc-500">Operations</p>
                        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Returns queue</h1>
                        <p className="mt-1 text-xs text-zinc-500">{total} return(s) match the current filter</p>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                        <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} data-testid="admin-returns-flagged-filter" />
                        <span className="inline-flex items-center gap-1"><ShieldAlert className="h-3 w-3 text-amber-700" /> Flagged only</span>
                    </label>
                </div>

                <div className="mb-4 flex flex-wrap gap-1.5" data-testid="admin-returns-tabs">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${status === s ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:bg-white/5 dark:text-zinc-300"}`}
                            data-testid={`admin-returns-tab-${s}`}
                        >
                            {s.replace("_", " ")}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-sm text-zinc-500">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">No returns in this view.</p>
                ) : (
                    <ul className="space-y-2" data-testid="admin-returns-list">
                        {items.map((r) => (
                            <li key={r.id}>
                                <Link
                                    to={`/admin/returns/${r.id}`}
                                    className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-100 bg-white p-4 transition-colors hover:border-brand-200 dark:bg-white/[0.03]"
                                    data-testid={`admin-return-row-${r.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-50">{r.order_no || r.order_id?.slice(0, 8)} · ₹{Number(r.requested_refund || 0).toFixed(2)}</p>
                                        <p className="mt-0.5 text-xs text-zinc-500">{r.items?.length || 0} item(s) • {new Date(r.created_at).toLocaleString()}</p>
                                    </div>
                                    {r.ai_validation?.flagged && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                            <ShieldAlert className="h-3 w-3" /> Flagged
                                        </span>
                                    )}
                                    <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_TONES[r.status] || "bg-zinc-100 text-zinc-700"}`}>{r.status.replace("_", " ")}</span>
                                    <ChevronRight className="h-4 w-4 text-zinc-400" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminReturns;
